import React, { useCallback, useContext, useMemo } from 'react';
import useTelemetry from './hooks/useTelemetry';
import { useMenu } from './hooks/useMenu';
import { useDashboardSettings } from './hooks/useDashboardSettings';
import useTelemetryConfirm from './hooks/useTelemetryConfirm';
import { useViewport } from './hooks/useViewport';
import useScenario from '../../../hooks/useScenario';
import { CommandCenterStateProviderContext } from '../../../context';
import {
  ico,
  TelemetryJumpToLiveButton,
  TelemetryGlobalZoomButton,
  TelemetryStartTimeInput,
  TelemetryTimeframeDropdown,
  resolveEffectiveSimulationDurationMinutes,
} from '../../../../CommonComponents/telemetry';
import WorkspaceTabItem from './components/workspace-tab-item';
import GridDashboard from './components/grid-dashboard';
import TelemetryLoading from './components/telemetry-loading';
import TelemetryNotFound from './components/telemetry-not-found';
import useUrlParams from '../../../hooks/useUrlParams';
import { SCENARIO_STATUS } from '../../../../simulate/constants';
import { CircularProgress } from '@mui/material';
import './telemetry-dashboard.css';
import CustomToolTip from '../../../../CommonComponents/CustomToolTip';
import { TelemetryModals } from './components';

export const TelemetryDashboard: React.FC = () => {
  const context = useContext(CommandCenterStateProviderContext);
  const { scenario } = useScenario();
  const { sat_id } = useUrlParams();

  const {
    dashboards,
    activeDashboard,
    activeTelemetryDashboardId,
    switchWorkspace,
    addWorkspace,
    renameWorkspace,
    deleteWorkspace,
    telemetryStatus,
    hasUnsavedChanges,
    saveWorkspace,
    isSavingWorkspace,
  } = useTelemetry();

  const {
    timeframe,
    setTimeframe,
    customStartTimeSec,
    setCustomStartTimeSec,
    toggleSettings,
  } = useDashboardSettings();

  const {
    isPaused,
    jumpToLive,
    viewport,
  } = useViewport(timeframe);

  const { openMenu, closeMenu } = useMenu();

  const {
    confirmWorkspaceDeletion,
  } = useTelemetryConfirm();

  const handleWorkspaceMenu = useCallback((e: React.MouseEvent, dbId: string, startInlineEdit: () => void) => {
    e.preventDefault();
    e.stopPropagation();
    const db = dashboards.find((d) => d.id === dbId);
    openMenu(
      e,
      [
        {
          label: 'Rename',
          icon: ico('edit'),
          onClick: () => {
            startInlineEdit();
            closeMenu();
          },
        },
        {
          label: 'Delete',
          icon: ico('trash'),
          danger: true,
          disabled: dashboards.length <= 1,
          onClick: () => {
            confirmWorkspaceDeletion(db?.name || 'Workspace', () => deleteWorkspace(dbId));
            closeMenu();
          },
        },
      ],
      { atPointer: true, width: 120 }
    );
  }, [dashboards, openMenu, closeMenu, confirmWorkspaceDeletion, deleteWorkspace]);

  const depStatus = context?.scenarioDeploymentDetails?.status || scenario?.status;
  const isSummaryView = depStatus === SCENARIO_STATUS.SIMULATION_COMPLETED || depStatus === 'SimulationCompleted';

  const simStartRaw = context?.scenarioDeploymentDetails?.simulationStartTime ?? scenario?.simulationStartTime;
  const simStartTimeMs = simStartRaw ? (simStartRaw < 2e9 ? simStartRaw * 1000 : simStartRaw) : null;

  const simulationDurationMinutes = useMemo(() => {
    const simStartSec = simStartRaw ? Math.floor(simStartRaw > 2e9 ? simStartRaw / 1000 : simStartRaw) : null;

    const selectedSat = context?.scenarioDeploymentDetails?.satellites?.find((sat) => sat.satelliteId === sat_id);
    const simTimeAbsRaw = selectedSat?.simulationTimeAbs || context?.scenarioDeploymentDetails?.satellites?.[0]?.simulationTimeAbs;
    const simTimeAbsSec = simTimeAbsRaw ? Math.floor(simTimeAbsRaw > 2e9 ? simTimeAbsRaw / 1000 : simTimeAbsRaw) : null;

    const liveDurationMinutes = context?.scenarioDeploymentDetails?.simulationDuration ?? scenario?.simulationDuration;

    return resolveEffectiveSimulationDurationMinutes(
      isSummaryView,
      simStartSec,
      simTimeAbsSec,
      liveDurationMinutes
    );
  }, [isSummaryView, simStartRaw, context?.scenarioDeploymentDetails, scenario, sat_id]);

  const isZoomed = Boolean(
    customStartTimeSec != null || (viewport && Math.abs(viewport.spanMs - timeframe * 60000) > 1000)
  );

  const handleJumpToLive = useCallback(() => {
    setCustomStartTimeSec(null);
    jumpToLive();
  }, [setCustomStartTimeSec, jumpToLive]);

  if (telemetryStatus === 'loading') {
    return <TelemetryLoading />;
  }

  if (telemetryStatus === 'not_found' || telemetryStatus === 'error' || !dashboards || dashboards.length === 0 || !activeDashboard) {
    return <TelemetryNotFound />;
  }

  return (
    <div className="telemetry-dashboard-container">
      {/* ===== WORKSPACE STRIP ===== */}
      <div className="workspace-tabs-container">
        <div className="workspace-tabs-wrapper">
          <div
            id="telemetry-workspace-tabs-scroll"
            className="workspace-tabs-scroll"
            onWheel={(e) => {
              if (e.deltaY !== 0) {
                e.currentTarget.scrollLeft += e.deltaY;
              }
            }}
          >
            {dashboards.map((db) => (
              <WorkspaceTabItem
                key={db.id}
                db={db}
                isActive={db.id === activeTelemetryDashboardId}
                onSelect={() => switchWorkspace(db.id)}
                onRename={renameWorkspace}
                onContextMenu={(e, startInlineEdit) => handleWorkspaceMenu(e, db.id, startInlineEdit)}
                onMenuClick={(e, startInlineEdit) => handleWorkspaceMenu(e, db.id, startInlineEdit)}
              />
            ))}
          </div>
          <CustomToolTip title="New Workspace">
            <button
              id="telemetry-add-workspace-btn"
              className="icon-btn icon-btn-sm workspace-add-btn"
              onClick={addWorkspace}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </button>
          </CustomToolTip>
        </div>

        {/* ===== GLOBAL CONTROLS ===== */}
        <div className="td-global-controls">
          {/* Start Time UTC Input Pill */}
          <TelemetryStartTimeInput
            valueSec={customStartTimeSec}
            onChange={setCustomStartTimeSec}
            simStartTimeMs={simStartTimeMs}
          />

          {/* Timeframe Dropdown (Filtered by simulationDurationMinutes) */}
          <TelemetryTimeframeDropdown
            timeframeMinutes={timeframe}
            onSelectTimeframe={(tf) => {
              setTimeframe(tf);
            }}
            simulationDurationMinutes={simulationDurationMinutes}
          />

          {/* Global Box Zoom Button */}
          <TelemetryGlobalZoomButton />

          {/* Jump to Live Button */}
          <TelemetryJumpToLiveButton
            isPaused={isPaused || customStartTimeSec != null}
            jumpToLive={handleJumpToLive}
            isZoomed={isZoomed}
          />

          {/* Settings / Dashboard Configuration */}
          <CustomToolTip title="Dashboard Configuration">
            <button
              className="td-toolbar-btn td-toolbar-icon-btn"
              onClick={toggleSettings}
              aria-label="Dashboard Configuration"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M5 2v12M11 2v12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                <circle cx="5" cy="10" r="1.8" stroke="currentColor" strokeWidth="1.3" fill="#080e12" />
                <circle cx="11" cy="6" r="1.8" stroke="currentColor" strokeWidth="1.3" fill="#080e12" />
              </svg>
            </button>
          </CustomToolTip>

          {/* Save Workspace */}
          <CustomToolTip title={"Save Dashboard"}>
            <div className="save-workspace-btn-wrap">
              <button
                className={`td-toolbar-btn td-save-btn ${hasUnsavedChanges && !isSavingWorkspace ? 'td-save-btn--active' : ''}`}
                disabled={!hasUnsavedChanges || isSavingWorkspace}
                onClick={() => {
                  saveWorkspace();
                }}
              >
                {isSavingWorkspace ? (
                  <span className="d-flex align-items-center gap-1">
                    Saving
                    <CircularProgress size={12} color="inherit" />
                  </span>
                ) : (
                  'Save'
                )}
              </button>
              {hasUnsavedChanges && !isSavingWorkspace && <span className="unsaved-indicator-dot" title="Unsaved changes" />}
            </div>
          </CustomToolTip>
        </div>
      </div>

      {/* ===== GRID VIEWPORT ===== */}
      {activeDashboard && <GridDashboard />}

      {/* ===== UNIFIED TELEMETRY MODALS ===== */}
      <TelemetryModals />
    </div>
  );
};

export default TelemetryDashboard;